import React, { useState } from 'react';

import { useFilteredTimezones } from './hooks/use-filtered-timezones';
import { TzList, TzTabButton } from './styles';

import { Dropdown, TextInput } from '@controlrooms/components';
import { TimeZone } from '@controlrooms/constants';

interface TimezoneSelectorV2Props {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
  plantTimezone?: string;
  disabled?: boolean;
}

export const TimezoneSelectorV2: React.FC<TimezoneSelectorV2Props> = ({
  selectedTimezone,
  onTimezoneChange,
  plantTimezone = 'UTC',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Use the custom hook for timezone filtering
  const {
    filteredTimezones,
    plantTimezoneData,
    computerTimezoneData,
    computerTimezone,
    getTimezoneAbbr,
  } = useFilteredTimezones({
    searchTerm,
    plantTimezone,
  });

  const handleTimezoneSelect = (timezone: TimeZone) => {
    if (disabled) return;
    const validTimezone = timezone.utc[0];
    onTimezoneChange(validTimezone);
    setIsOpen(false);
  };

  const handlePlantTimezoneSelect = () => {
    if (disabled) return;
    onTimezoneChange(plantTimezone);
    setIsOpen(false);
  };

  const handleComputerTimezoneSelect = () => {
    if (disabled) return;
    onTimezoneChange(computerTimezone);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (disabled) return;
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  };

  const formatOffset = (offset: number) => {
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset);
    const minutes = (absOffset - hours) * 60;

    if (minutes === 0) {
      return `GMT${sign}${hours.toString().padStart(2, '0')}:00`;
    }
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getTimezoneDisplayName = (timezone: TimeZone) => {
    // Use the text field which contains the full timezone name
    return timezone.textWithoutOffset || timezone.value;
  };

  const getCityRegion = (timezone: TimeZone) => {
    // Extract city/region from the timezone name if available
    const displayName = getTimezoneDisplayName(timezone);

    // Common patterns for city/region extraction
    const cityPatterns = [
      / - ([^-]+)$/, // "Timezone Name - City"
      / \(([^)]+)\)$/, // "Timezone Name (City)"
      /, ([^,]+)$/, // "Timezone Name, City"
    ];

    for (const pattern of cityPatterns) {
      const match = displayName.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return null;
  };

  const getTimezoneName = (timezone: TimeZone) => {
    const displayName = getTimezoneDisplayName(timezone);
    const city = getCityRegion(timezone);

    if (city) {
      // Remove the city part to get just the timezone name
      return displayName
        .replace(/ - [^-]+$/, '')
        .replace(/ \([^)]+\)$/, '')
        .replace(/, [^,]+$/, '');
    }

    return displayName;
  };

  return (
    <Dropdown.Root
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      {/* Trigger Button */}
      <TzTabButton onClick={handleToggle} style={{ width: '100px', justifyContent: 'center' }}>
        {getTimezoneAbbr(selectedTimezone)}
      </TzTabButton>

      {/* Dropdown Options */}
      <Dropdown.Options open={isOpen} position="right" style={{ width: '350px' }}>
        {/* Search Input */}
        <div
          style={{
            padding: '12px',
            boxSizing: 'border-box',
          }}
        >
          <TextInput
            placeholder="Search timezones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </div>

        {/* Timezone List */}
        <TzList>
          {/* Plant Timezone */}
          {plantTimezoneData && (
            <Dropdown.Item
              onClick={handlePlantTimezoneSelect}
              active={selectedTimezone === plantTimezone}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                lineHeight: '1.4',
              }}
            >
              <div
                style={{
                  minWidth: '70px',
                  marginRight: '12px',
                }}
              >
                ({formatOffset(plantTimezoneData.offset)})
              </div>
              <div
                style={{
                  flex: 1,
                }}
              >
                <div style={{ fontWeight: '500' }}>Plant ({plantTimezoneData.abbr})</div>
              </div>
              <div
                style={{
                  marginLeft: '12px',
                }}
              >
                {plantTimezoneData.abbr}
              </div>
            </Dropdown.Item>
          )}

          {/* Divider */}
          <Dropdown.Separator />

          {/* Computer Timezone */}
          {computerTimezoneData && (
            <Dropdown.Item
              onClick={handleComputerTimezoneSelect}
              active={selectedTimezone === computerTimezone}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                lineHeight: '1.4',
              }}
            >
              <div
                style={{
                  minWidth: '70px',
                  marginRight: '12px',
                }}
              >
                ({formatOffset(computerTimezoneData.offset)})
              </div>
              <div
                style={{
                  flex: 1,
                }}
              >
                <div style={{ fontWeight: '500' }}>My Computer ({computerTimezoneData.abbr})</div>
              </div>
              <div
                style={{
                  marginLeft: '12px',
                }}
              >
                {computerTimezoneData.abbr}
              </div>
            </Dropdown.Item>
          )}

          {/* Divider */}
          <Dropdown.Separator />

          {/* Other Timezones */}
          {filteredTimezones.map((timezone, index) => {
            const isSelected = timezone.utc[0] === selectedTimezone;
            const timezoneName = getTimezoneName(timezone);
            const cityRegion = getCityRegion(timezone);
            const offset = formatOffset(timezone.offset);

            return (
              <Dropdown.Item
                key={`${timezone.value}-${timezone.offset}-${index}`}
                onClick={() => handleTimezoneSelect(timezone)}
                active={isSelected}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  lineHeight: '1.4',
                }}
              >
                {/* GMT Offset */}
                <div
                  style={{
                    minWidth: '70px',
                    marginRight: '12px',
                  }}
                >
                  ({offset})
                </div>

                {/* Timezone Name and City */}
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <div style={{ fontWeight: '500' }}>
                    {timezoneName}
                    {cityRegion && <span> - {cityRegion}</span>}
                  </div>
                </div>

                {/* Abbreviation */}
                <div
                  style={{
                    marginLeft: '12px',
                  }}
                >
                  {timezone.abbr}
                </div>
              </Dropdown.Item>
            );
          })}
        </TzList>

        {/* No results message */}
        {filteredTimezones.length === 0 && (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
            }}
          >
            No timezones found
          </div>
        )}
      </Dropdown.Options>
    </Dropdown.Root>
  );
};
